<template>
  <div class="control-bar" :class="{ visible: visible }" role="toolbar" :aria-label="t('player.accessibility.controlBar')" @click.stop>
    <!-- Progress Bar -->
    <ProgressBar
      :current-time="player.currentTime.value"
      :duration="player.duration.value"
      :trim-start="player.trimStart.value"
      :trim-end="player.trimEnd.value"
      :buffered="player.buffered.value"
      :chapters="showChapters !== false ? player.chapters.value : []"
      :overlays="overlays"
      :interactive="player.canSeek.value"
      @seek="player.seek"
    />

    <!-- Controls Container -->
    <div class="controls-container">
      <!-- Left Controls -->
      <div class="controls-left">
        <PreviousVideoButton
          v-if="player.totalItems.value > 1"
          class="control-button--step"
          :disabled="!player.canGoPrev.value"
          @click="player.prev"
        />

        <PlayPauseButton
          :is-playing="player.isPlaying.value"
          @click="player.togglePlay"
        />

        <!-- Nothing but a move of the playhead, so not drawn while the viewer may not make one. -->
        <template v-if="player.canSeek.value">
          <SkipBackwardButton class="control-button--skip" @click="handleSkipBackward" />
          <SkipForwardButton class="control-button--skip" @click="handleSkipForward" />
        </template>

        <NextVideoButton
          v-if="player.totalItems.value > 1"
          class="control-button--step"
          :disabled="!player.canGoNext.value"
          @click="() => player.next()"
        />

        <VolumeControl
          :volume="player.volume.value"
          :is-muted="player.isMuted.value"
          @volume-change="player.setVolume"
          @toggle-mute="player.toggleMute"
        />

        <!-- Time Display -->
        <div class="time-display" role="timer">
          <span class="current-time">{{ formatTime(player.displayCurrentTime.value) }}</span>
          <span class="time-separator" aria-hidden="true">/</span>
          <span class="total-time">{{ formatTime(player.displayDuration.value) }}</span>
        </div>
      </div>

      <!-- Right Controls -->
      <div class="controls-right">
        <SubtitlesButton
          v-if="player.availableSubtitleTracks.value.length > 0"
          :player="player"
        />

        <TranscriptButton
          v-if="player.availableSubtitleTracks.value.length > 0"
          :player="player"
        />

        <PlaylistButton
          v-if="player.totalItems.value > 1 && (!collectionDisplayMode || collectionDisplayMode === 'drawer')"
          :player="player"
        />

        <ChaptersButton
          v-if="showChapters !== false && player.chapters.value.length > 0"
          :player="player"
        />

        <DownloadButton
          v-if="player.hasDownloads.value"
          :player="player"
        />

        <!-- PiP -->
        <button
          v-if="enablePip !== false && supportsPiP"
          class="control-button control-button--pip"
          @click="$emit('togglePip')"
          :aria-label="t('player.controls.pip')"
          :data-tooltip="t('player.controls.pip')"
          v-html="pictureInPictureIcon"
        />

        <!-- TODO: Enable when Cast/AirPlay features are fully implemented -->
        <!-- <CastButton :player="player" /> -->
        <!-- <AirPlayButton :player="player" /> -->

        <SettingsButton :player="player" />

        <FullscreenButton
          v-if="enableFullscreen !== false"
          :is-fullscreen="player.isFullscreen.value"
          @click="player.toggleFullscreen"
        />

        <!-- Logo (in control bar, far right) -->
        <component
          v-if="logoUrl"
          :is="logoClickUrl ? 'a' : 'div'"
          class="control-bar-logo"
          :href="logoClickUrl || undefined"
          :target="logoClickUrl ? '_blank' : undefined"
          :rel="logoClickUrl ? 'noopener noreferrer' : undefined"
          @click.stop
        >
          <img
            :src="logoUrl"
            alt=""
            class="control-bar-logo__img"
          />
        </component>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import type { CollectionDisplayMode } from '../../types'
import type { OverlayConfig } from '../../../types/embed'
import { pictureInPictureIcon } from '../../assets/icons'
import { formatTime } from '../../utils/time'
import { useVideoI18n } from '../../i18n'
import PlayPauseButton from './PlayPauseButton.vue'
import SkipBackwardButton from './SkipBackwardButton.vue'
import SkipForwardButton from './SkipForwardButton.vue'
import NextVideoButton from './NextVideoButton.vue'
import PreviousVideoButton from './PreviousVideoButton.vue'
import FullscreenButton from './FullscreenButton.vue'
import ProgressBar from './ProgressBar.vue'
import ChaptersButton from './ChaptersButton.vue'
import DownloadButton from './DownloadButton.vue'
import PlaylistButton from './PlaylistButton.vue'
import SettingsButton from './SettingsButton.vue'
import SubtitlesButton from './SubtitlesButton.vue'
import TranscriptButton from './TranscriptButton.vue'
import VolumeControl from './VolumeControl.vue'
// TODO: Enable when Cast/AirPlay features are fully implemented
// import CastButton from './CastButton.vue'
// import AirPlayButton from './AirPlayButton.vue'

const props = defineProps<{
  visible?: boolean
  player: UseVideoPlayerReturn
  collectionDisplayMode?: CollectionDisplayMode
  enablePip?: boolean
  enableFullscreen?: boolean
  showChapters?: boolean
  overlays?: OverlayConfig[]
  logoUrl?: string
  logoClickUrl?: string
}>()

defineEmits<{
  togglePip: []
}>()

const { t } = useVideoI18n()

const supportsPiP = computed(() =>
  typeof document !== 'undefined' && document.pictureInPictureEnabled,
)

function handleSkipBackward(): void {
  props.player.seek(props.player.currentTime.value - 10)
}

function handleSkipForward(): void {
  props.player.seek(props.player.currentTime.value + 10)
}
</script>

<style scoped lang="scss">
/* Styles in main.scss > _control-bar.scss */
</style>
